import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormArray, FormControl } from '@angular/forms';
import { AlertController, ModalController } from '@ionic/angular';
import { EntityProperty } from '@tcc-mono/shared/data-access';
import { LoaderService } from '@tcc-mono/shared/ui/services';
import { PropertyValuePipe, upsertObjectIntoArray, withLoading } from '@tcc-mono/shared/utils';
import {
  Relation,
  Stable,
  KoppelTypeSettings,
  stableRelationVisitFromRelation,
} from '@tcc-mono/vandijk/shared/data-access/models';
import { RvoUbnInfoService } from '@tcc-mono/vandijk/shared/data-access/vandijk-api';
import { SelectCustomerDialogComponent } from '@tcc-mono/vandijk/shared/ui/entity-selectors/select-customer-dialog';
import { GTSKV_COUNTRY_OPTIONS } from '@tcc-mono/vandijk/shared/ui/constants';
import { StableRepository } from '@tcc-mono/vandijk/web/crm/data-access';
import { from, tap, switchMap, map, filter, take } from 'rxjs';

@Component({
  templateUrl: './stable-dialog.component.html',
  styleUrls: ['./stable-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [PropertyValuePipe]
})
export class StableDialogComponent implements OnInit {
  private readonly _fb = inject(FormBuilder);
  private readonly _modalCtrl = inject(ModalController);
  private readonly _alertCtrl = inject(AlertController);
  private readonly _cdr = inject(ChangeDetectorRef);
  private readonly _repo = inject(StableRepository);
  private readonly _rvoUbnInfo = inject(RvoUbnInfoService);
  private readonly _loading = inject(LoaderService);
  private readonly _propertyPipe = inject(PropertyValuePipe);

  @Input() public stable: Stable = new Stable();
  public form: FormGroup;
  public readonly countryOptions = GTSKV_COUNTRY_OPTIONS;

  // Predefined koppel types
  public readonly predefinedKoppelTypes = [
    { key: 'witvlees', label: 'Witvlees' },
    { key: 'starters', label: 'Starters' },
    { key: 'rose', label: 'Rose' }
  ];

  public ngOnInit(): void {
    this.form = this._initForm();
    this.form.get('ubn')?.valueChanges.subscribe(() => this._cdr.markForCheck());
  }

  public selectRelation(): void {
    from(
      this._modalCtrl.create({
        component: SelectCustomerDialogComponent,
        componentProps: {},
        cssClass: 'stacked-modal modal-min-height'
      })
    )
      .pipe(
        tap(modal => modal.present()),
        switchMap(modal => modal.onDidDismiss()),
        map(({ data }) => data as Relation),
        filter((relation: Relation) => !!relation),
        tap((relation: Relation) => {
          this.form.get('relation_id').setValue(relation?.id);
          Object.assign(this.stable, stableRelationVisitFromRelation(relation));
          this._cdr.detectChanges();
        }),
        take(1)
      )
      .subscribe();
  }

  public close(stable?: Stable): void {
    this._modalCtrl.dismiss(stable);
  }

  public copyVisitAddress(): void {
    if (this.stable.relation_visit_address) {
      this.form.get('address').setValue(this.stable.relation_visit_address);
    }
    if (this.stable.relation_visit_zipcode) {
      this.form.get('zipcode').setValue(this.stable.relation_visit_zipcode);
    }
    if (this.stable.relation_visit_city) {
      this.form.get('city').setValue(this.stable.relation_visit_city);
    }
    if (this.stable.relation_visit_country) {
      this.form.get('country').setValue(this.stable.relation_visit_country);
    }
    if (this.stable.relation_visit_phone) {
      this.form.get('phone').setValue(this.stable.relation_visit_phone);
    }
    this._cdr.detectChanges();
  }

  public canFetchAddressFromUbn(): boolean {
    const ubnControl = this.form?.get('ubn');
    const ubn = String(ubnControl?.value ?? '').trim();
    return !!ubn && !ubnControl?.invalid;
  }

  public fetchAddressFromUbn(): void {
    if (!this.canFetchAddressFromUbn()) {
      return;
    }

    const ubn = String(this.form.get('ubn')?.value ?? '').trim();

    this._rvoUbnInfo
      .ubnInfo(ubn)
      .pipe(take(1), withLoading(this._loading))
      .subscribe({
        next: (items) => {
          const details = items[0];
          if (!details) {
            void this._showAlert('Geen RVO-gegevens', `Geen adres gevonden voor UBN ${ubn}.`);
            return;
          }

          const address = details.adresOmsME?.trim() || '';
          const zipcode = details.postcodeME?.trim() || '';
          const city = details.plaatsnaamME?.trim() || '';

          if (!address && !zipcode && !city) {
            void this._showAlert('Geen adres', `RVO heeft geen adresgegevens voor UBN ${ubn}.`);
            return;
          }

          if (address) {
            this.form.get('address')?.setValue(address);
          }
          if (zipcode) {
            this.form.get('zipcode')?.setValue(zipcode);
          }
          if (city) {
            this.form.get('city')?.setValue(city);
          }
          this.form.get('country')?.setValue('NL');
          this._cdr.detectChanges();
        },
        error: (err) => {
          const message = err?.error?.message || 'Adres kon niet worden opgehaald bij RVO.';
          void this._showAlert('Fout', message);
        }
      });
  }

  private async _showAlert(header: string, message: string): Promise<void> {
    const alert = await this._alertCtrl.create({
      header,
      message,
      buttons: ['OK']
    });
    await alert.present();
  }

  public get koppelTypeSettingsArray(): FormArray {
    return this.form.get('koppel_type_settings') as FormArray;
  }

  public addKoppelTypeSetting(): void {
    const newSetting = this._fb.group({
      key: ['', Validators.required],
      value: [0, [Validators.required, Validators.min(0)]]
    });
    this.koppelTypeSettingsArray.push(newSetting as any);
    this._cdr.detectChanges();
  }

  public removeKoppelTypeSetting(index: number): void {
    this.koppelTypeSettingsArray.removeAt(index);
    this._cdr.detectChanges();
  }

  public addPredefinedKoppelType(type: { key: string; label: string }): void {
    // Check if this type already exists
    const existingIndex = this.koppelTypeSettingsArray.controls.findIndex(
      control => control.get('key').value === type.key
    );

    if (existingIndex === -1) {
      const newSetting = this._fb.group({
        key: [type.key, Validators.required],
        value: [0, [Validators.required, Validators.min(0)]]
      });
      this.koppelTypeSettingsArray.push(newSetting as any);
      this._cdr.detectChanges();
    }
  }

  public getAvailablePredefinedTypes(): { key: string; label: string }[] {
    const usedKeys = this.koppelTypeSettingsArray.controls.map(
      control => control.get('key').value
    );
    return this.predefinedKoppelTypes.filter(type => !usedKeys.includes(type.key));
  }


  public save = (): void => {
    if (this.form.invalid) {
      this.form.markAllAsTouched();

      // Log form errors for debugging
      console.log('Form is invalid:', this.form.errors);
      console.log('Form controls with errors:', this._getFormErrors());

      // Scroll to first error field
      this._scrollToFirstError();

      return;
    }

    const fv: { [key: string]: any } = this.form.value;

    const props: EntityProperty[] = [
      {
        code: 'UBN',
        value: fv['ubn']
      },
      {
        code: 'RVO_NUMBER',
        value: fv['rvo_number']
      },
      {
        code: 'RVO_USER',
        value: fv['rvo_user']
      },
      {
        code: 'RVO_PASSWORD',
        value: fv['rvo_password']
      },
    ];

    const properties = upsertObjectIntoArray(this.stable?.properties || [], props, 'code')
      .filter((el) => !(el.code === 'RVO_PASSWORD' && el.value === 'xxxxxx'));

    // Convert koppel type settings array to object
    const koppelTypeSettings: KoppelTypeSettings = {};
    const koppelSettingsArray = fv['koppel_type_settings'] || [];
    koppelSettingsArray.forEach((setting: { key: string; value: number }) => {
      if (setting.key && setting.value !== null && setting.value !== undefined) {
        koppelTypeSettings[setting.key] = setting.value;
      }
    });

    const stableToSave: Stable = {
      ...this.stable,
      name: fv['name'],
      address: fv['address'],
      zipcode: fv['zipcode'],
      city: fv['city'],
      country: fv['country'],
      phone: fv['phone'],
      remarks: fv['remarks'],
      relation_id: fv['relation_id'],
      weide_toeslag: fv['weide_toeslag'],
      vitaal_kalf_participant: fv['vitaal_kalf_participant'],
      rvo_eigenbeheer: fv['rvo_eigenbeheer'],
      is_gtskv_loadingplace: fv['is_gtskv_loadingplace'],
      skv_capacity: fv['skv_capacity'] !== '' && fv['skv_capacity'] != null ? fv['skv_capacity'] : null,
      properties: properties,
      koppel_type_settings: Object.keys(koppelTypeSettings).length > 0 ? koppelTypeSettings : {}
    };

    const call$ = stableToSave.id ? this._repo.updateStable(stableToSave.id, stableToSave) : this._repo.addStable(stableToSave);

    call$
      .pipe(
        take(1),
        withLoading(this._loading)
      )
      .subscribe({
        next: (stable: Stable) => this.close(stable),
        error: () => this.close()
      });
  };

  private _scrollToFirstError(): void {
    // Wait for DOM to update after markAllAsTouched
    setTimeout(() => {
      // Find first invalid input field
      const firstInvalidInput = document.querySelector('ion-input.ion-invalid');

      if (firstInvalidInput) {
        firstInvalidInput.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      } else {
        // Fallback: scroll to error summary if no invalid inputs found
        const errorSummary = document.querySelector('.error-summary');
        if (errorSummary) {
          errorSummary.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
        }
      }
    }, 100);
  }

  private _getFormErrors(): any {
    const errors: any = {};

    // Check main form controls
    Object.keys(this.form.controls).forEach(key => {
      const control = this.form.get(key);
      if (control && control.errors) {
        errors[key] = control.errors;
      }
    });

    // Check koppel type settings array
    const koppelArray = this.form.get('koppel_type_settings') as FormArray;
    if (koppelArray) {
      koppelArray.controls.forEach((control, index) => {
        if (control.errors) {
          errors[`koppel_type_settings[${index}]`] = control.errors;
        }
        // Check nested controls
        if (control.get('key')?.errors) {
          errors[`koppel_type_settings[${index}].key`] = control.get('key').errors;
        }
        if (control.get('value')?.errors) {
          errors[`koppel_type_settings[${index}].value`] = control.get('value').errors;
        }
      });
    }

    return errors;
  }

  private _initForm(): FormGroup {
    // Initialize koppel type settings FormArray
    const koppelTypeSettingsArray = this._fb.array([]);

    // Populate with existing koppel type settings
    if (this.stable?.koppel_type_settings) {
      Object.entries(this.stable.koppel_type_settings).forEach(([key, value]) => {
        koppelTypeSettingsArray.push(this._fb.group({
          key: [key, Validators.required],
          value: [value, [Validators.required, Validators.min(0)]]
        }) as any);
      });
    }

    return this._fb.group({
      relation_id: [this.stable?.relation_id, [Validators.required]],
      name: [this.stable?.name, [Validators.required]],
      ubn: [this._propertyPipe.transform(this.stable?.properties, 'UBN'), [Validators.pattern(/^[0-9]*$/), Validators.max(2147483647)]],
      address: [this.stable?.address, [Validators.required]],
      zipcode: [this.stable?.zipcode, [Validators.required]],
      city: [this.stable?.city, [Validators.required]],
      country: [this.stable?.country ?? 'NL', [Validators.required]],
      phone: [this.stable?.phone],
      remarks: [this.stable?.remarks],
      rvo_number: [this._propertyPipe.transform(this.stable?.properties, 'RVO_NUMBER')],
      rvo_user: [this._propertyPipe.transform(this.stable?.properties, 'RVO_USER')],
      rvo_password: [this._propertyPipe.transform(this.stable?.properties, 'RVO_PASSWORD')],
      weide_toeslag: [this.stable?.weide_toeslag ?? false, [Validators.required]],
      vitaal_kalf_participant: [this.stable?.vitaal_kalf_participant ?? false, [Validators.required]],
      rvo_eigenbeheer: [this.stable?.rvo_eigenbeheer ?? false, [Validators.required]],
      is_gtskv_loadingplace: [this.stable?.is_gtskv_loadingplace ?? false, [Validators.required]],
      skv_capacity: [this.stable?.skv_capacity ?? null, [Validators.min(0)]],
      koppel_type_settings: koppelTypeSettingsArray
    });
  }

}
